import { useState, useRef } from 'react';
import { Button } from '@/components/atoms/ui/button';
import { FiShuffle, FiImage } from 'react-icons/fi';
import { COLOR_PALETTES } from '@/components/molecules/ColorPicker';
import { rgbToHex } from '@/lib/utils';

interface PaletteEditorProps {
  palette: string[];
  onPaletteChange: (palette: string[]) => void;
  disabled?: boolean;
}

// Helper functions for color operations
function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  return [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16),
  ];
}

function colorDistance(
  rgb1: [number, number, number],
  rgb2: [number, number, number]
): number {
  const [r1, g1, b1] = rgb1;
  const [r2, g2, b2] = rgb2;
  return Math.sqrt((r2 - r1) ** 2 + (g2 - g1) ** 2 + (b2 - b1) ** 2);
}

// Convert RGB to HSL (returns [hue: 0-360, saturation: 0-1, lightness: 0-1])
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return [h * 360, s, l];
}

// K-means clustering to reduce colors to 8
function kMeans(colors: string[], k: number, maxIterations = 10): string[] {
  // Convert colors to RGB
  const colorRgbs = colors.map((color) => hexToRgb(color));

  // Initialize centroids deterministically (sorted by hue first for better results)
  const centroids: [number, number, number][] = [];

  // First, sort colors by hue to get a more predictable initial state
  const sortedByHue = [...colorRgbs].sort((a, b) => {
    const hslA = rgbToHsl(a[0], a[1], a[2]);
    const hslB = rgbToHsl(b[0], b[1], b[2]);
    return hslA[0] - hslB[0];
  });

  // Select evenly spaced colors from the sorted array
  const step = Math.max(1, Math.floor(sortedByHue.length / k));
  for (let i = 0; i < k && i * step < sortedByHue.length; i++) {
    centroids.push([...sortedByHue[i * step]]);
  }

  // If we don't have enough centroids, fill with remaining colors
  while (centroids.length < k && centroids.length < sortedByHue.length) {
    centroids.push([...sortedByHue[centroids.length]]);
  }

  // K-means iterations
  for (let iteration = 0; iteration < maxIterations; iteration++) {
    // Assign each color to nearest centroid
    const clusters: number[][] = Array(k)
      .fill(0)
      .map(() => []);

    colorRgbs.forEach((rgb, idx) => {
      let minDistance = Infinity;
      let nearestCentroid = 0;

      centroids.forEach((centroid, cIdx) => {
        const distance = colorDistance(rgb, centroid);
        if (distance < minDistance) {
          minDistance = distance;
          nearestCentroid = cIdx;
        }
      });

      clusters[nearestCentroid].push(idx);
    });

    // Update centroids (average of assigned colors)
    let centroidsChanged = false;
    clusters.forEach((cluster, cIdx) => {
      if (cluster.length === 0) return; // Skip empty clusters

      let sumR = 0,
        sumG = 0,
        sumB = 0;
      cluster.forEach((colorIdx) => {
        const rgb = colorRgbs[colorIdx];
        sumR += rgb[0];
        sumG += rgb[1];
        sumB += rgb[2];
      });

      const newCentroid: [number, number, number] = [
        Math.round(sumR / cluster.length),
        Math.round(sumG / cluster.length),
        Math.round(sumB / cluster.length),
      ];

      const oldCentroid = centroids[cIdx];
      if (
        oldCentroid[0] !== newCentroid[0] ||
        oldCentroid[1] !== newCentroid[1] ||
        oldCentroid[2] !== newCentroid[2]
      ) {
        centroidsChanged = true;
      }
      centroids[cIdx] = newCentroid;
    });

    if (!centroidsChanged) break;
  }

  // Convert centroids back to hex colors
  const hexColors = centroids.map((centroid) =>
    rgbToHex(centroid[0], centroid[1], centroid[2]).toUpperCase()
  );

  // Fill remaining slots with white if we have less than k colors
  while (hexColors.length < k) {
    hexColors.push('#FFFFFF');
  }

  // Sort colors by hue (rainbow order) then by lightness
  const sortedColors = [...hexColors].sort((a, b) => {
    const rgbA = hexToRgb(a);
    const rgbB = hexToRgb(b);

    const hslA = rgbToHsl(rgbA[0], rgbA[1], rgbA[2]);
    const hslB = rgbToHsl(rgbB[0], rgbB[1], rgbB[2]);

    const [hueA, satA, lightA] = hslA;
    const [hueB, satB, lightB] = hslB;

    // Handle grayscale colors (low saturation) - put them at the end
    const isGrayA = satA < 0.1;
    const isGrayB = satB < 0.1;

    if (isGrayA && !isGrayB) return 1;
    if (!isGrayA && isGrayB) return -1;
    if (isGrayA && isGrayB) {
      // Both are grayscale, sort by lightness (light to dark)
      return lightB - lightA;
    }

    // Both are colored, sort by hue first (rainbow order)
    const normalizedHueA = isNaN(hueA) ? 0 : hueA;
    const normalizedHueB = isNaN(hueB) ? 0 : hueB;

    const hueDiff = normalizedHueA - normalizedHueB;
    if (Math.abs(hueDiff) > 5) {
      return hueDiff;
    }

    // If hues are very similar, sort by lightness (lighter first for better visual flow)
    return lightB - lightA;
  });

  return sortedColors;
}

export const PaletteEditor: React.FC<PaletteEditorProps> = ({
  palette,
  onPaletteChange,
  disabled = false,
}) => {
  const [selectedPaletteIndex, setSelectedPaletteIndex] = useState(
    Math.floor(Math.random() * COLOR_PALETTES.length)
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleColorChange = (index: number, color: string) => {
    const newPalette = [...palette];
    // Ensure color is valid hex
    const hexColor = color.startsWith('#') ? color : `#${color}`;
    if (/^#[0-9A-F]{6}$/i.test(hexColor)) {
      newPalette[index] = hexColor.toUpperCase();
      onPaletteChange(newPalette);
    }
  };

  const handleRandomizePalette = () => {
    // Select a random palette index, ensuring it's different from current
    let newIndex;
    do {
      newIndex = Math.floor(Math.random() * COLOR_PALETTES.length);
    } while (newIndex === selectedPaletteIndex && COLOR_PALETTES.length > 1);
    setSelectedPaletteIndex(newIndex);
    // Update palette with the new palette colors
    onPaletteChange(COLOR_PALETTES[newIndex].colors.map((c) => c));
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      return;
    }

    try {
      const image = new Image();
      const imageUrl = URL.createObjectURL(file);

      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = imageUrl;
      });

      // Create canvas to read pixel data
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Could not get canvas context');
      }

      ctx.drawImage(image, 0, 0);
      const imageData = ctx.getImageData(0, 0, image.width, image.height);
      const data = imageData.data;

      // Collect unique colors
      const colorSet = new Set<string>();
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        // Skip fully transparent pixels
        if (a < 128) continue;

        const hex = rgbToHex(r, g, b).toUpperCase();
        colorSet.add(hex);
      }

      const colors = Array.from(colorSet);

      // Reduce to 8 colors using k-means
      const reducedColors = kMeans(colors, 8);

      onPaletteChange(reducedColors);

      URL.revokeObjectURL(imageUrl);
    } catch (error) {
      console.error('Image processing error:', error);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    if (draggedIndex !== index) {
      const newPalette = [...palette];
      const draggedColor = newPalette[draggedIndex];
      newPalette.splice(draggedIndex, 1);
      newPalette.splice(index, 0, draggedColor);
      onPaletteChange(newPalette);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="palette-editor-component space-y-4">
      {/* Palette Colors - 8x1 grid */}
      <div className="grid grid-cols-8 gap-2 p-4 rounded-lg border border-border bg-muted">
        {palette.map((color, idx) => (
          <div
            key={idx}
            className="relative aspect-square"
            draggable={!disabled}
            onDragStart={() => handleDragStart(idx)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDragEnd={handleDragEnd}
          >
            <input
              type="color"
              value={color}
              onChange={(e) => handleColorChange(idx, e.target.value)}
              className="w-full h-full rounded-md border-2 border-border hover:border-primary cursor-pointer transition-all hover:scale-105"
              style={{
                backgroundColor: color,
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
              }}
              disabled={disabled}
              title={`Click to change: ${color}`}
            />
          </div>
        ))}
      </div>

      {/* Randomize Button */}
      <Button
        type="button"
        variant="outline"
        onClick={handleRandomizePalette}
        disabled={disabled}
        className="w-full"
      >
        <FiShuffle className="mr-2 h-4 w-4" />
        Randomize Palette
      </Button>

      {/* Or Divider */}
      <div className="relative flex items-center justify-center py-2">
        <div className="relative bg-background px-2 text-sm text-muted-foreground">
          or
        </div>
      </div>

      {/* Image Upload */}
      <div className="flex flex-col items-center gap-2">
        <div className="relative w-full">
          <input
            id="palette-image"
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageUpload}
            disabled={disabled}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
          />
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
          >
            <FiImage className="mr-2 h-4 w-4" />
            Choose Image
          </Button>
        </div>
      </div>
    </div>
  );
};

